import React, { useContext, useEffect, useState, useMemo } from 'react';
import CardContainer from 'components/redesign/CardContainer';
import { useOverview } from 'hooks/overview-hook';
import { FilterContext } from 'components/FilterBar/FilterProvider';
import { ThemeContext } from '../../context/ThemeContext';
import useCustomMediaQuery from 'utils/useCustomMediaQuery';
import HeatmapView from './HeatmapView';
import BarChartView from './BarChartView';
import {
  processMetricsData,
  transformMetricsToDefinitions,
} from './helper/valueCompare';
import { screenSizes, constants } from 'constants/Constants';
import { useDynamicGraphQLQuery } from 'hooks/useDynamicGraphQLQuery';
import { helperFunctions } from 'utils/helperFunctions';
import { ToggleWrapper } from '../../v2/components/GDS/Toggle';

interface ValueComparisonHOCProps {
  slug: string;
  title: string;
}

const ValueComparisonHOC: React.FC<ValueComparisonHOCProps> = ({
  slug,
  title,
}) => {
  // Always call hooks at the top level.
  const { selectedProduct } = useContext(FilterContext);
  const { mode } = useContext(ThemeContext);
  const isMobileForValueComparison = useCustomMediaQuery(screenSizes.mobile);

  // Declare selectedView early.
  const [selectedView, setSelectedView] = useState('Bar Chart');
  const toggleItems = ['Bar Chart', 'Heatmap'];
  const handleToggleChange = (item: string) => {
    setSelectedView(item);
  };

  const { convertMetricsToSelectOptions, isDevOrQaEnv } = helperFunctions();

  // States for fetched data.
  const [globalFilterComparionResData, setGlobalFilterComparionResData] =
    useState(null);
  const [globalFilterHeatMapData, setGlobalFilterHeatMapData] = useState(null);

  // GraphQL query names based on view.
  const comparionQueryName =
    selectedView === 'Bar Chart' ? 'getGlobalFilterComparionRes' : null;
  const heatMapQueryName =
    selectedView === 'Heatmap' ? 'getGlobalFilterHeatMap' : null;

  const { data: comparisonResponse, isLoading: comparisonIsLoading } =
    useDynamicGraphQLQuery(comparionQueryName);
  const { data: heatMapResponse, isLoading: heatMapIsLoading } =
    useDynamicGraphQLQuery(heatMapQueryName);

  useEffect(() => {
    if (comparisonResponse) {
      setGlobalFilterComparionResData(
        comparisonResponse?.getGlobalFilterComparionRes?.data
      );
    }
  }, [comparisonResponse]);

  useEffect(() => {
    if (heatMapResponse) {
      setGlobalFilterHeatMapData(heatMapResponse?.getGlobalFilterHeatMap?.data);
    }
  }, [heatMapResponse]);

  // Compute chart data based on view.
  const chartData = processMetricsData(
    globalFilterComparionResData,
    mode,
    isMobileForValueComparison
  );
  const transformedDataDefinitions =
    selectedView === 'Bar Chart'
      ? transformMetricsToDefinitions(globalFilterComparionResData)
      : selectedView === 'Heatmap'
        ? transformMetricsToDefinitions(globalFilterHeatMapData)
        : null;

  // Only render for PepsiConnect product.
  const shouldRender =
    selectedProduct?.productId === constants.products.PepsiConnect;

  // Fetch master metric list from the API using useOverview.
  const { metricLocalFiltersData, getMetricLocalFiltersData } = useOverview();

  useEffect(() => {
    if (
      selectedProduct?.productId &&
      selectedProduct?.productId === constants.products.PepsiConnect
    ) {
      getMetricLocalFiltersData({
        variables: { productId: selectedProduct?.productId },
      });
    }
  }, [selectedProduct?.productId, getMetricLocalFiltersData]);

  // Memoize the metric options from the API data.
  const metricOptions = useMemo(
    () => convertMetricsToSelectOptions(metricLocalFiltersData?.metrics),
    [metricLocalFiltersData?.metrics]
  );

  // Lift state for the selected metric.
  const [selectedMetric, setSelectedMetric] = useState(
    metricOptions && metricOptions.length > 0 ? metricOptions[0] : null
  );

  useEffect(() => {
    if (metricOptions && metricOptions.length > 0) {
      const exists = metricOptions.find(
        (opt) => opt.value === selectedMetric?.value
      );
      if (!exists) {
        setSelectedMetric(metricOptions[0]);
      }
    }
  }, [metricOptions]);

  const chartViews = {
    'Bar Chart': (
      <BarChartView
        chartData={chartData}
        isLoading={comparisonIsLoading}
        mode={mode}
      />
    ),
    Heatmap: (
      <HeatmapView
        heatMapData={globalFilterHeatMapData}
        isLoading={heatMapIsLoading}
        selectedMetric={selectedMetric}
        setSelectedMetric={setSelectedMetric}
        metricOptions={metricOptions}
        mode={mode}
      />
    ),
  };

  // Create toggle control for header
  const toggleControl = isDevOrQaEnv() ? (
    <ToggleWrapper
      label={toggleItems[0]} // Show current selection
      checked={selectedView === toggleItems[1]} // Reflect state
      size="small"
      variant="horizontal"
      value={toggleItems[1]}
      onChange={(checked) =>
        handleToggleChange(checked ? 'Heatmap' : 'Bar Chart')
      }
    />
  ) : null;

  const headerControls = toggleControl ? [toggleControl] : [];

  return shouldRender ? (
    <CardContainer
      slug={slug}
      title={title}
      showInfoIcon={true}
      dataDefinitions={{ sections: transformedDataDefinitions }}
      isDraggable={false}
      isLoading={
        selectedView === 'Bar Chart' ? comparisonIsLoading : heatMapIsLoading
      }
      isAccordion={false}
      headerControls={headerControls}
    >
      {chartViews[selectedView]}
    </CardContainer>
  ) : null;
};

export default ValueComparisonHOC;
